<script lang="ts" setup>
import { VIcon, VInput, VLoader, VButton } from "@app/core";
import { useVuelidate } from "@vuelidate/core";
import { maxLength, minLength, required } from "@vuelidate/validators";
import { computed, onMounted, reactive, ref } from "vue";

import { useSavedFilters } from "../composables/useSavedFilters";

import { useModal } from "@/shared/composables";

const {
  sortedFilters,
  isLoading,
  isCreating,
  isDeleting,
  isTogglingFavorite,
  fetchFilters,
  createFilter,
  deleteFilter,
  toggleFavorite,
  applyFilter,
  buildCurrentFilterLink,
} = useSavedFilters();

const { close: closeDrawer } = useModal("global-filter");

// UI State
const isExpanded = ref(false);
const isCreateMode = ref(false);
const filter = reactive({
  name: "",
});
const rules = {
  name: { required, minLength: minLength(3), maxLength: maxLength(100) },
};
const v$ = useVuelidate(rules, filter);

// Available filter params that can be excluded
interface FilterParam {
  key: string
  label: string
  excluded: boolean
}

const filterParams = ref<FilterParam[]>([]);

// Build filter params from current URL
const buildFilterParams = () => {
  const query = window.location.search;
  const params = new URLSearchParams(query);

  const paramLabels: Record<string, string> = {
    primary_since: "Primary Date (Since)",
    primary_until: "Primary Date (Until)",
    secondary_since: "Comparison Date (Since)",
    secondary_until: "Comparison Date (Until)",
    brands: "Brands",
    channels: "Channels",
    products: "Products",
    granularity: "Granularity",
  };

  // Group date params
  const hasDateParams = params.has("primary_since") || params.has("primary_until");
  const hasComparisonParams = params.has("secondary_since") || params.has("secondary_until");

  const result: FilterParam[] = [];

  if (hasDateParams) {
    result.push({
      key: "dates",
      label: "Date Range",
      excluded: false,
    });
  }

  if (hasComparisonParams) {
    result.push({
      key: "comparison",
      label: "Comparison Date",
      excluded: false,
    });
  }

  // Add other params
  const paramKeys = ["brands", "channels", "products", "granularity"];
  for (const key of paramKeys) {
    const value = params.get(key);
    if (value) {
      result.push({
        key,
        label: paramLabels[key] || key,
        excluded: false,
      });
    }
  }

  filterParams.value = result;
};

// Get excluded params for link building
const getExcludedParams = (): string[] => {
  const excluded: string[] = [];

  for (const param of filterParams.value) {
    if (param.excluded) {
      if (param.key === "dates") {
        excluded.push("primary_since", "primary_until");
      }
      else if (param.key === "comparison") {
        excluded.push("secondary_since", "secondary_until");
      }
      else {
        excluded.push(param.key);
      }
    }
  }

  return excluded;
};

// Toggle param inclusion
const toggleParam = (param: FilterParam) => {
  param.excluded = !param.excluded;
};

// Open create mode
const openCreateMode = () => {
  buildFilterParams();
  filter.name = "";
  isCreateMode.value = true;
};

// Close create mode
const closeCreateMode = () => {
  isCreateMode.value = false;
  filter.name = "";
  filterParams.value = [];
  v$.value.$reset();
};

// Save new filter
const handleSave = async () => {
  await v$.value.$validate();
  if (v$.value.$error) return;

  const link = buildCurrentFilterLink(getExcludedParams());

  const success = await createFilter({
    name: filter.name.trim(),
    link,
    favorite: false,
  });

  if (success) {
    v$.value.$reset();
    closeCreateMode();
    closeDrawer();
  }
};

// Apply filter and close drawer
const handleApply = (filter: Parameters<typeof applyFilter>[0]) => {
  applyFilter(filter);
  closeDrawer();
};

// Computed: active params count (not excluded)
const activeParamsCount = computed(() => {
  return filterParams.value.filter(p => !p.excluded).length;
});

// Parse saved filter link to show what's inside
interface ParsedFilterInfo {
  icon: string
  label: string
  value: string
}

const parseFilterLink = (link: string): ParsedFilterInfo[] => {
  const result: ParsedFilterInfo[] = [];
  const params = new URLSearchParams(link.startsWith("?") ? link.slice(1) : link);

  // Brands
  const brands = params.get("brands");
  if (brands) {
    const brandList = brands.split(",");
    result.push({
      icon: "lucide:building-2",
      label: "Brands",
      value: brandList.length > 2
        ? `${brandList.slice(0, 2).join(", ")} +${brandList.length - 2}`
        : brandList.join(", "),
    });
  }

  // Channels
  const channels = params.get("channels");
  if (channels) {
    const channelList = channels.split(",");
    result.push({
      icon: "lucide:store",
      label: "Channels",
      value: `${channelList.length} channel${channelList.length > 1 ? "s" : ""}`,
    });
  }

  // Products
  const products = params.get("products");
  if (products) {
    const productList = products.split(",");
    result.push({
      icon: "lucide:package",
      label: "Products",
      value: `${productList.length} product${productList.length > 1 ? "s" : ""}`,
    });
  }

  // Granularity
  const granularity = params.get("granularity");
  if (granularity) {
    result.push({
      icon: "lucide:calendar",
      label: "Granularity",
      value: granularity.charAt(0) + granularity.slice(1).toLowerCase(),
    });
  }

  // Date Range
  if (params.has("primary_since") || params.has("primary_until")) {
    result.push({
      icon: "lucide:calendar-range",
      label: "Date",
      value: "Custom range",
    });
  }

  return result;
};

// Load filters on mount
onMounted(() => {
  fetchFilters();
});
</script>

<template>
  <div class="saved-filters">
    <!-- Header -->
    <button
      class="w-full flex items-center justify-between py-2 text-left"
      type="button"
      @click="isExpanded = !isExpanded"
    >
      <div class="flex items-center gap-2">
        <VIcon
          :size="18"
          class="text-primary"
          icon="lucide:bookmark"
        />
        <span class="text-sm font-medium text-mainText">Saved Filters</span>
        <span
          v-if="sortedFilters.length > 0"
          class="text-xs text-secondaryText bg-base-200 px-1.5 py-0.5 rounded"
        >
          {{ sortedFilters.length }}
        </span>
      </div>
      <VIcon
        :class="{ 'rotate-180': isExpanded }"
        :size="16"
        class="text-secondaryText transition-transform duration-200"
        icon="lucide:chevron-down"
      />
    </button>

    <!-- Content -->
    <Transition name="expand">
      <div
        v-show="isExpanded"
        class="mt-2 space-y-3"
      >
        <!-- Loading -->
        <div
          v-if="isLoading"
          class="flex justify-center py-4"
        >
          <VLoader size="small" />
        </div>

        <!-- Create Mode -->
        <div
          v-else-if="isCreateMode"
          class="space-y-3"
        >
          <VInput
            v-model="filter.name"
            :validation="v$.name"
            name="filter-name"
            placeholder="Enter filter name"
            @keydown.enter="handleSave"
          />

          <!-- Filter params to include/exclude -->
          <div
            v-if="filterParams.length > 0"
            class="space-y-2"
          >
            <p class="text-xs text-secondaryText">
              Include in filter (click to exclude):
            </p>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="param in filterParams"
                :key="param.key"
                :class="[
                  param.excluded
                    ? 'bg-base-200 text-secondaryText line-through'
                    : 'bg-primary/10 text-primary',
                ]"
                class="
                inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs
                transition-all duration-150
              "
                type="button"
                @click="toggleParam(param)"
              >
                <span>{{ param.label }}</span>
                <VIcon
                  v-if="!param.excluded"
                  :size="12"
                  class="opacity-60 hover:opacity-100"
                  icon="lucide:x"
                />
              </button>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex gap-2">
            <VButton
              :disabled="!filter.name.trim() || activeParamsCount === 0"
              :loading="isCreating"
              class="flex-1"
              size="sm"
              text="Save"
              variant="primary"
              @click="handleSave"
            />
            <VButton
              class="flex-1"
              size="sm"
              text="Cancel"
              variant="default"
              @click="closeCreateMode"
            />
          </div>
        </div>

        <!-- Filters List -->
        <template v-else>
          <!-- Empty state -->
          <div
            v-if="sortedFilters.length === 0"
            class="text-center py-4"
          >
            <VIcon
              :size="32"
              class="text-secondaryText mb-2"
              icon="lucide:bookmark-x"
            />
            <p class="text-sm text-secondaryText">
              No saved filters yet
            </p>
          </div>

          <!-- Filters -->
          <TransitionGroup
            v-else
            class="space-y-1 max-h-64 overflow-y-auto"
            name="filter-list"
            tag="div"
          >
            <div
              v-for="filter in sortedFilters"
              :key="filter.id"
              class="
              group flex items-center gap-2 p-2 rounded-lg
              hover:bg-base-200 transition-colors cursor-pointer
            "
              @click="handleApply(filter)"
            >
              <!-- Favorite -->
              <button
                :class="filter.favorite ? 'text-warning' : 'text-secondaryText'"
                :disabled="isTogglingFavorite === filter.id"
                class="flex-shrink-0 p-1 rounded hover:bg-base-300 transition-colors"
                type="button"
                @click.stop="toggleFavorite(filter.id)"
              >
                <!-- Star icon with animation -->
                <VIcon
                  :loading="isTogglingFavorite === filter.id"
                  :size="14"
                  icon="lucide:star"
                />
              </button>

              <!-- Name & Date -->
              <div class="flex-1 min-w-0">
                <p class="text-sm text-mainText truncate font-medium">
                  {{ filter.name }}
                </p>
                <!-- Filter contents preview -->
                <div class="flex flex-wrap gap-1 mt-1">
                  <span
                    v-for="(info, idx) in parseFilterLink(filter.link)"
                    :key="idx"
                    class="inline-flex items-center gap-0.5 text-xs text-secondaryText"
                  >
                    <VIcon
                      :icon="info.icon"
                      :size="10"
                      class="opacity-70"
                    />
                    <span class="truncate max-w-20">{{ info.value }}</span>
                    <span
                      v-if="idx < parseFilterLink(filter.link).length - 1"
                      class="text-cardBorder mx-0.5"
                    >•</span>
                  </span>
                </div>
              </div>

              <!-- Delete -->
              <button
                class="
                flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100
                hover:bg-lightError hover:text-error transition-all
              "
                type="button"
                @click.stop="deleteFilter(filter.id)"
              >
                <VIcon
                  :loading="isDeleting === filter.id"
                  :size="14"
                  icon="lucide:trash-2"
                />
              </button>
            </div>
          </TransitionGroup>

          <!-- Add Button -->
          <VButton
            class="w-full"
            icon="lucide:plus"
            size="sm"
            text="Save Current Filters"
            @click="openCreateMode"
          />
        </template>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.saved-filters {
  border-top: 1px solid theme('colors.cardBorder');
  padding-top: 1rem;
}

/* Expand animation */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.2s ease-out;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* TransitionGroup animations */
.filter-list-move,
.filter-list-enter-active,
.filter-list-leave-active {
  transition: all 0.3s ease;
}

.filter-list-enter-from,
.filter-list-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.filter-list-leave-active {
  position: absolute;
}
</style>
